import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { CanceledError } from 'axios';
import PaginationParameters from 'common/contracts/pagination-parameters';
import Guid from 'common/values/guid/guid';
import { useEffect, useState } from 'react';
import CreateNetworkedGroupApiRequest from 'users/entities/networked-group/api/request-contracts/create-networked-group-api-request';
import NetworkedGroupSelector from 'users/entities/networked-group/view/networked-group-selector';
import UserNetworkConnectionProfile from 'users/entities/user-network-connection/user-network-connection-profile';
import { useSession } from 'users/session/session-context';
import UserCoworkerQuery, { UserCoworkerFilterParameters, UserCoworkerOrderParameters } from 'users/values/user-coworker/api/request-contracts/query-user-coworker-api-request';
import UserCoworkerApiService from 'users/values/user-coworker/api/user-coworker-api-service';
import UserCoworker from 'users/values/user-coworker/user-coworker';

const ContentContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down("sm")]: {
      columnCount: 1,
    },
    [theme.breakpoints.up("md")]: {
      columnCount: 2,
    },
    [theme.breakpoints.up("lg")]: {
      columnCount: 3,
    },
    columnFill: "balance",
    columnGap: theme.spacing(4),
    gridColumn: "1 / auto",
    gridRowStart: "2",
    paddingTop: theme.spacing(0.75),
  }));
const StyledTextField = styled(TextField)(
  ({ theme }) => ({
    marginBottom: theme.spacing(2),
    width: "100%",
  }));
const StyledSubmitButton = styled(Button)(
  ({ theme }) => ({
    marginTop: theme.spacing(2),
    width: "50%",
  }));


export interface CreateNetworkedGroupComponentProps {
  onCreate: (request: CreateNetworkedGroupApiRequest | null) => void;
}

export default function CreateNetworkedGroupComponent(props: Readonly<CreateNetworkedGroupComponentProps>) {
  const { onCreate } = props;

  const [groupName, setGroupName] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [selectedMembers, setSelectedMembers] = useState<Guid[]>([]);
  const [coworkers, setCoworkers] = useState<UserCoworker[]>([]);
  const [networkConnections, setNetworkConnections] = useState<UserNetworkConnectionProfile[]>([]);

  const session = useSession();

  useEffect(() => {
    const abortController = new AbortController();
    setNetworkConnections(session?.networkConnections);
    void loadCoworkers(abortController);
    return () => {
      abortController.abort();
    };
  }, []);

  async function loadCoworkers(abortController: AbortController) {
    if (!session.user?.companyEntityId) return;
    try {
      const paginationParameters = new PaginationParameters(0, 100);
      const userCoworkerService = new UserCoworkerApiService(session);
      const filterParameters = new UserCoworkerFilterParameters();
      const orderParameters = new UserCoworkerOrderParameters();

      const coworkerQuery = new UserCoworkerQuery(
        filterParameters,
        orderParameters,
        paginationParameters
      );

      const paginatedResponse = await userCoworkerService.getUserCoworkers(
        coworkerQuery,
        abortController
      );

      setCoworkers(paginatedResponse.data);
    }
    catch (error: unknown) {
      if (error instanceof CanceledError) return;
      console.error("Failed to load coworkers:", error);
    }
  }

  function generateDefaultGroupName(): string {
    if (selectedMembers.length === 0) return "New Group";
    const selectedMemberNames = selectedMembers.map(id => {
      const coworker = coworkers.find(c => c.userId === id);
      const connection = networkConnections.find(c => c.user.id === id);
      return coworker?.name || connection?.user.fullName || "Unknown";
    });
    return `Chat with ${selectedMemberNames.join(", ")}`;
  }

  return (
    <div>
      <ContentContainer>
        <StyledTextField
          label="Group Name"
          variant="outlined"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
        />
        <StyledTextField
          label="Description"
          variant="outlined"
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <NetworkedGroupSelector
          coworkers={coworkers}
          networkConnections={networkConnections}
          selectedMemberUserIds={selectedMembers}
          onMemberSelected={(id: Guid) => {
            setSelectedMembers((prev) => {
              if (prev.some((memberId) => memberId.value === id.value)) {
                return prev.filter((memberId) => memberId.value !== id.value);
              }
              return [...prev, id];
            });
          }}
        />
      </ContentContainer>
      <StyledSubmitButton
        variant="contained"
        color="primary"
        disabled={selectedMembers.length === 0}
        onClick={() => {
          const request = new CreateNetworkedGroupApiRequest(
            groupName ?? generateDefaultGroupName(),
            description,
            selectedMembers
          );
          onCreate(request);
        }}>
        Create Group
      </StyledSubmitButton>
    </div>
  );
}
